/**
 * 도메인 데이터 쿼리 키.
 *
 * 두 화면이 같은 데이터를 보면서 키를 따로 적으면, 한쪽에서 저장해도 다른 쪽이 갱신되지 않는다.
 * v1 에서 화면마다 fetch 를 다시 짜다 생긴 문제라 v2 는 키를 한 곳에 모은다.
 *
 * 세션·메뉴처럼 인증에 딸린 키는 각 모듈이 갖고 있다(`sessionQueryKey`, `menuSettingsQueryKey`).
 */
export const queryKeys = {
  weeks: ['weeks'] as const,
  departments: ['departments'] as const,
  /** 부서 + 소속 인원·붙는 역할. 부서 관리 화면만 본다(메뉴 권한이 걸린 경로다). */
  departmentsAdmin: ['departments', 'admin'] as const,
  sites: ['sites'] as const,
  /** 내 현장 즐겨찾기 — 현장 id 목록(V320). 계정별이라 키에 id 를 붙이지 않는다: 계정을 바꾸면
   *  세션 무효화와 함께 캐시가 통째로 비워진다. */
  siteFavorites: ['site-favorites'] as const,
  hqCafeteriaMenus: ['hq-cafeteria-menus'] as const,
  siteCafeteriaMenus: ['site-cafeteria-menus'] as const,
  siteRestaurants: ['site-restaurants'] as const,
  requestBoardPosts: ['request-board-posts'] as const,
  /**
   * 윤리경영제보센터 — 한 쪽씩 받는다. 호출부가 `[...queryKeys.thanksPosts, page]` 로 늘려 쓰고,
   * 제보를 내면 접두어로 <b>전부</b> 무효화한다(새 제보가 뒤쪽 줄을 한 칸씩 민다).
   *
   * ★ 키 이름은 `thanks-*` 그대로다. 화면 이름이 바뀌었다고 캐시 키를 갈면 옛 키로 담긴 응답이
   *   남아 도는데, 그것을 정리할 자리가 어디에도 없다(V259 에서 라벨만 바꿨다).
   */
  thanksPosts: ['thanks-posts'] as const,
  /** 제보 하나. 호출부가 `[...queryKeys.thanksPost, postId]` 로 늘려 쓴다 —
   *  주소로 바로 들어온 사람에게는 목록 캐시가 없다. */
  thanksPost: ['thanks-post'] as const,
  /** 그 제보의 답변. 호출부가 `[...queryKeys.thanksComments, postId]` 로 늘려 쓴다 — 연 제보만 읽는다. */
  thanksComments: ['thanks-comments'] as const,
  hqTaskItems: ['hq-task-items'] as const,
  siteTaskItems: ['site-task-items'] as const,
  weeklyExecutiveNotes: ['weekly-executive-notes'] as const,
  dashboardOrders: ['dashboard-orders'] as const,
  monthlyMeetingCategories: ['monthly-meeting-categories'] as const,
  monthlyMeetingEvents: ['monthly-meeting-events'] as const,
  monthlyMeetingExceptions: ['monthly-meeting-exceptions'] as const,
  /** 통합 캘린더의 연차. 구간마다 다른 결과라 호출부가 `[...queryKeys.calendarLeaves, from, to]` 로 늘려 쓴다. */
  calendarLeaves: ['calendar-leaves'] as const,
  personnel: ['personnel'] as const,
  /** 직원찾기 모달의 조직도 트리. 전 직원이 같은 트리를 보므로 사용자 키를 붙이지 않는다. */
  directoryTree: ['directory-tree'] as const,
  /** 직원 한 명의 명함. 호출부가 `[...queryKeys.directoryPerson, personnelId]` 로 늘려 쓴다. */
  directoryPerson: ['directory-person'] as const,
  /** 명함의 「전결표 업무」 모달. `[...queryKeys.directoryMatrixDuties, personnelId]`. */
  directoryMatrixDuties: ['directory-matrix-duties'] as const,
  /** 명함의 「담당 업무」 모달 — 업무분장(V599). `[...queryKeys.directoryDutyAssignments, personnelId]`. */
  directoryDutyAssignments: ['directory-duty-assignments'] as const,
  /** 인사자료 › 업무분장 표(V599). */
  dutyAssignments: ['hr-duty-assignments'] as const,
  /** 업무분장 보이기 스위치 둘(현장 조회 담당 업무 · 소속 불일치). 전사 값이다. */
  dutyDisplay: ['hr-duty-display'] as const,
  /** 현장 조회 › 조직도 탭 아래 「담당 업무」. `[...queryKeys.siteDuties, siteId]`. */
  siteDuties: ['site-duties'] as const,
  /** 아직 안 끝난 휴직 — 조직도·배치표·직원찾기의 이름 옆 배지. 휴직을 고치면 함께 무효화한다. */
  directoryAbsences: ['directory-absences'] as const,
  /** 본사 평면도가 올라와 있는가. 도면 자체가 아니라 <b>있는지 여부와 파일명</b>만 담는 가벼운 값이다. */
  directoryFloorplan: ['directory-floorplan'] as const,
  /**
   * PDF 한 개 위에 적어 둔 것(글자·형광펜·상자·선). 자료실 뷰어와 자리배치도가 <b>같은 키</b>를 본다 —
   * 같은 파일을 어느 문으로 열든 같은 것이 보여야 한다(V74). 호출부가
   * `[...queryKeys.docAnnotations, source, connectionId, ref]` 로 늘려 쓴다.
   */
  docAnnotations: ['doc-annotations'] as const,
  personnelCareers: ['personnel-careers'] as const,
  /** 한 사람의 기술등급 세부정보. 호출부가 `[...queryKeys.techGrade, personnelId]` 로 늘려 쓴다. */
  techGrade: ['tech-grade'] as const,
  personnelAssignments: ['personnel-assignments'] as const,
  /** 인사발령 이력. 호출부가 `[...queryKeys.placementOrders, personnelId, limit]` 로 늘려 쓴다. */
  placementOrders: ['placement-orders'] as const,
  /** 전술판을 편집할 수 있는가(서버 판정). 계정이 바뀌면 세션 무효화와 함께 캐시가 비워진다. */
  placementAuthority: ['placement-authority'] as const,
  /** 본사 자리배치도 — 두 층의 자리와 앉은 사람(V392). 인력배치표의 자리배치도 보기와 직원찾기 명함이 같은 키를 본다. */
  hqSeats: ['hq-seats'] as const,
  /** 계정에 붙었지만 아직 배치되지 않은 소속 — 인력배치표 위의 승인 대기 배너(V189). */
  placementSuggestions: ['placement-suggestions'] as const,
  noticePosts: ['notice-posts'] as const,
  /**
   * 지금 홈에 세울 팝업 공지(V251). 게시판 목록(`noticePosts`)과 <b>키를 갈랐다</b> —
   * 같은 표를 읽지만 담기는 것이 다르다. 이쪽은 「기간이 남은 것」 이라 시간이 지나면 저절로
   * 줄고, 저쪽은 그 게시판의 글 전부다.
   */
  noticePopups: ['notice-popups'] as const,
  /**
   * 나에게 아직 남은 투표 — 홈 모달이 본다(V208).
   *
   * 관리 화면의 목록(`polls`)과 <b>키를 갈랐다</b>. 같은 표를 읽지만 담기는 것이 다르다 —
   * 이쪽은 「내가 안 한 것」 이라 사람마다 다르고, 저쪽은 만든 것 전부다. 한 키로 묶으면
   * 관리 화면을 다녀온 뒤 홈이 남의 목록을 들고 있게 된다.
   */
  pollsPending: ['polls-pending'] as const,
  /** 마감했고 내가 참여한 투표 — 결과는 마감 뒤에만 열린다(V208). */
  pollResults: ['poll-results'] as const,
  /** 투표 관리 화면의 목록(만든 것 전부). */
  polls: ['polls'] as const,
  /** 공지 댓글. 호출부가 `[...queryKeys.noticeComments, postId]` 로 늘려 쓴다 — 글을 펼친 것만 읽는다. */
  noticeComments: ['notice-comments'] as const,
  /** 홈 「보미소식」 위젯 — 오늘 생일자와 최근 경조사. */
  bomiNews: ['bomi-news'] as const,
  assetNoticePosts: ['asset-notice-posts'] as const,
  employeeNewsItems: ['employee-news-items'] as const,
  users: ['users'] as const,
  constructionTrendPosts: ['construction-trend-posts'] as const,
  constructionTrendSources: ['construction-trend-sources'] as const,
  internalGuidelinePosts: ['internal-guideline-posts'] as const,
  technicalCasePosts: ['technical-case-posts'] as const,
  newTechnologyPosts: ['new-technology-posts'] as const,
  bomiPerformancePosts: ['bomi-performance-posts'] as const,
  todoItems: ['todo-items'] as const,
  /** 내가 내야 하는 교육 증빙(V264). 본인 것뿐이라 키에 id 를 붙이지 않는다 — 계정을 바꾸면
   *  세션 무효화와 함께 캐시가 통째로 비워진다. */
  techEducationEvidenceMine: ['tech-education-evidence-mine'] as const,
  /** 치운 파생 할일 키. 화면이 스스로 계산하는 파생(결재 대기 등)을 거르는 데 쓴다(V231). */
  todoDismissals: ['todo-dismissals'] as const,
  /** 작업 기록(DOC-100 §9) — 사람·기간으로 갈리므로 부르는 쪽이 뒤에 붙인다. */
  todoLedger: ['todo-ledger'] as const,
  todoLedgerPeople: ['todo-ledger-people'] as const,
  /** 개선사항 요청. 서버가 보는 사람에 따라 범위를 가르므로(관리자=전체, 그 외=본인) 키는 하나로 둔다 —
   *  계정을 바꾸면 세션 무효화와 함께 캐시가 통째로 비워진다. */
  improvementRequests: ['improvement-requests'] as const,
  /** 내 소속·인사 요약(`/v1/home/profile`). 로그인한 사람 본인 것뿐이라 키에 id 를 붙이지 않는다 —
   *  계정을 바꾸면 세션 무효화와 함께 캐시가 통째로 비워진다. */
  homeProfile: ['home-profile'] as const,
  /** 내 담당업무(V437). 마이페이지가 읽고 고친다. */
  myDuties: ['my-duties'] as const,
  /** 마이페이지 「내 업무분장」(V599). 본인 것뿐이라 키에 id 를 붙이지 않는다. */
  myDutyAssignments: ['my-duty-assignments'] as const,
  /** 내 역할의 지금 이름·층(`/v1/me/access`). 본인 것뿐이라 키에 id 를 붙이지 않는다 —
   *  계정을 바꾸면 세션 무효화와 함께 캐시가 통째로 비워진다. */
  myAccess: ['me-access'] as const,
  /** 연차 현황 표. 호출부가 `[...queryKeys.leaveBoard, year, month]` 로 늘려 쓴다 —
   *  보이는 줄의 범위는 서버가 정하므로 키에 사람을 넣지 않는다(계정을 바꾸면 캐시가 비워진다). */
  leaveBoard: ['hr-leave-board'] as const,
  /** 내가 속한 회의만(`/v1/meeting-events?mine=true`). 전체 목록과 캐시를 나눈다. */
  myMeetingEvents: ['meeting-events', 'mine'] as const,
  /**
   * 오늘 내 회의 — <b>서버가 펼친 회차</b>(`/v1/home/meetings/today`). 반복 규칙과 그날만의
   * 예외를 서버가 함께 본다. 인사말과 위젯이 같은 것을 읽어야 두 곳이 다른 말을 하지 않는다(DOC-66 §6).
   */
  myMeetingsToday: ['meetings', 'today', 'mine'] as const,
  /** 이번 주·다음 주 내 회의 — 오늘 회의 위젯(`/v1/home/meetings/weeks`). 펼치는 계산은 위와 같다. */
  myMeetingsWeeks: ['meetings', 'weeks', 'mine'] as const,
  /**
   * 홈 인사말. 사용자마다 다르므로 `[...queryKeys.homeGreeting, userId]` 로 늘려 쓴다.
   * 유효 기간은 서버가 응답에 실어 준다(`expiresAt`) — 여기서 정하지 않는다.
   */
  homeGreeting: ['home-greeting'] as const,
  /** AI 인사말 켬/끔. 위젯 관리 모달이 편집한다. */
  homeGreetingPreference: ['home-greeting-preference'] as const,
  /**
   * 지점은 서버가 로그인 사용자의 대표 소속에서 정한다. 그래서 캐시는 지점명이 아니라
   * `[...queryKeys.weather, userId]` 로 가른다 — 계정을 바꾸면 남의 현장 날씨가 남지 않는다.
   */
  weather: ['weather'] as const,
  /**
   * 환율·자재 단가 위젯. 날씨와 달리 <b>사용자 키를 붙이지 않는다</b> — 고시 환율도 회사가 정한
   * 단가도 사람마다 달라지지 않는다(서버도 캐시 한 칸을 전원이 나눠 쓴다).
   */
  market: ['market'] as const,
  /** 뉴스 섹션 탭. 관리 화면은 `[...queryKeys.newsSections, 'all']` 로 끈 섹션까지 읽는다. */
  newsSections: ['news-sections'] as const,
  /** 섹션별 기사. 호출부가 `[...queryKeys.newsItems, sectionId]` 로 늘려 쓴다. */
  newsItems: ['news-items'] as const,
  /** AI Agent 대화 목록(사이드바). */
  aiAgentSessions: ['ai-agent-sessions'] as const,
  /** 대화 하나의 본문. 호출부가 `[...queryKeys.aiAgentSession, id]` 로 늘려 쓴다. */
  aiAgentSession: ['ai-agent-session'] as const,
  /** 에이전트가 지금 할 수 있는 일(빈 화면 제안 칩). 사람마다 다르지 않아 사용자 키를 붙이지 않는다. */
  aiAgentCapabilities: ['ai-agent-capabilities'] as const,
  /** 업무 묶음 목록(AI Agent 모드의 [루틴] 탭 · DOC-133). 코드가 정한 목록이라 사용자 키가 없다. */
  aiAgentRecipes: ['ai-agent-recipes'] as const,
  /** 내 바깥 AI(MCP) 연결 목록(DOC-134). 본인 것만 오는 경로라 사용자 키를 붙이지 않는다. */
  aiAgentMcpConnections: ['ai-agent-mcp-connections'] as const,
  /** 떠 있는 AI 오브의 색·자리. 회사 전체에 한 벌이라 사용자 키를 붙이지 않는다. */
  agentOrbDesign: ['agent-orb-design'] as const,
  /**
   * 오브가 지금 할 수 있는 말 몇 줄(홈 인사말 신호 엔진). 색·자리와 <b>키를 나눈다</b> — 저쪽은
   * 회사에 한 벌이라 오래 들고 있어도 되지만 이쪽은 <b>사람마다 다르고</b> 십 분마다 새로 받는다.
   * 키를 합치면 색을 저장할 때마다 말 묶음까지 버려진다.
   */
  orbLines: ['orb-lines'] as const,
  /** 실험실 장비 제원 정본 + 근거(DOC-27 §14). 호출부가 `[...queryKeys.labMachineSpecs, machineClass]` 로 늘려 쓴다. */
  labMachineSpecs: ['lab-machine-specs'] as const,
  /** 헤더 바로가기(홈페이지·블로그·그룹웨어…). 회사 전체에 하나뿐이라 사용자 키를 붙이지 않는다. */
  shortcutLinks: ['shortcut-links'] as const,
  /**
   * 외부 API 키 상태(V144). <b>키 값은 담기지 않는다</b> — 설정 여부·출처·뒤 네 자리뿐이다.
   * 회사 전체에 하나뿐이라 사용자 키를 붙이지 않는다.
   */
  externalApiKeys: ['external-api-keys'] as const,
  /**
   * 메뉴가 바깥으로 나가는 주소(외부 링크). <b>비밀이 아니라 눌러서 가는 곳</b>이라 값이 그대로 담긴다.
   * 사이드바·라우트·설정 화면이 이 하나를 나눠 본다 — 저장하면 세 곳이 함께 새 주소를 가리킨다.
   */
  externalLinks: ['external-links'] as const,
  /**
   * 팝빌 홈택스수집 연동 상태(V300). 부서사용자 로그인 확인이 붙어 있어 <b>실제 바깥 호출 한 번</b>이다 —
   * 외부 API 키 상태와 따로 두어, 키를 저장할 때마다 홈택스를 두드리지 않게 한다.
   */
  hometaxStatus: ['hometax-status'] as const,
  /**
   * 수집해 담아 둔 매출 세금계산서. 호출부가 <code>[...queryKeys.hometaxInvoices, from, to]</code> 로
   * 늘려 쓴다 — 기간이 곧 다른 목록이라 이전 기간이 잠깐 비치면 안 된다.
   */
  hometaxInvoices: ['hometax-invoices'] as const,
  /**
   * 팝빌 <b>발행</b> 연동 상태(V307). 수집 상태와 따로 둔다 — 키가 다 있어도 <b>공동인증서</b>가
   * 없으면 발행만 거절되고, 그 확인은 수집이 부르지 않는 다른 호출이다.
   */
  taxInvoiceStatus: ['tax-invoice-status'] as const,
  /**
   * 우리가 끊은 세금계산서. 호출부가 <code>[...queryKeys.taxInvoiceIssued, from, to]</code> 로
   * 늘려 쓴다 — 기간이 곧 다른 목록이다.
   */
  taxInvoiceIssued: ['tax-invoice-issued'] as const,
  /**
   * 팝빌 <b>알림톡</b> 연동 상태. 키 넷은 세금계산서와 같지만 상품이 달라 따로 둔다 —
   * 이 조회 하나에 발신프로필·발신번호·템플릿 목록이 함께 실린다(셋이 따로 비어 있을 수 있다).
   */
  alimTalkStatus: ['alimtalk-status'] as const,
  /**
   * 알림톡 템플릿 한 벌 — <b>본문과 치환변수</b>. 호출부가
   * <code>[...queryKeys.alimTalkTemplate, code]</code> 로 늘려 쓴다: 템플릿을 바꾸면 변수 칸이
   * 통째로 다른 것이라 이전 템플릿의 본문이 잠깐 비치면 안 된다.
   */
  alimTalkTemplate: ['alimtalk-template'] as const,
  /** 퇴사자 설문 양식(V146). 회사 전체에 하나뿐이라 사용자 키를 붙이지 않는다. */
  exitSurveyForm: ['exit-survey-form'] as const,

  /**
   * 총무 취합 대장(DOC-43 §14 2단계). 필터가 곧 키다 — 상태·월을 바꾸면 다른 목록이라
   * 이전 목록이 잠깐 비치면 안 된다. 접두어(`ga-…`)로 묶어 두어 한 화면의 변경이
   * 그 화면 것만 다시 읽게 한다.
   */
  gaBusinessCards: (status: string) => ['ga-business-cards', status] as const,
  /** 결재가 안 끝난 명함 신청. 대장(`ga-business-cards`)과 함께 무효화된다 — 승인이 둘을 한꺼번에 옮긴다. */
  gaBusinessCardsPending: ['ga-business-cards', 'pending'] as const,
  gaBusinessCardSettings: ['ga-business-card-settings'] as const,
  /** 직급·직책·부서의 영문 표기. 명함 발주서가 국문 아래에 함께 찍는다. */
  gaEnglishTerms: ['ga-english-terms'] as const,
  /** 명함 양식(PDF)과 값이 들어갈 자리. */
  gaCardTemplates: ['ga-card-templates'] as const,
  gaCardTemplateFields: ['ga-card-template-fields'] as const,
  gaBusinessCardBatches: ['ga-business-card-batches'] as const,
  /** 발주처(명함 업체·케이스 업체 · V203). 케이스는 다른 업체라 한 목록을 종류로 나눠 쓴다. */
  gaOrderVendors: ['ga-order-vendors'] as const,
  gaExpenseStandards: (scopeType: string) => ['ga-expense-standards', scopeType] as const,
  gaExpenseRequests: (targetMonth: string, scopeType: string) =>
    ['ga-expense-requests', targetMonth, scopeType] as const,
  gaCertificates: ['ga-certificates'] as const,
  gaCertificateIssues: (outstandingOnly: boolean) => ['ga-certificate-issues', outstandingOnly] as const,
  gaCourierRequests: (targetMonth: string) => ['ga-courier', 'requests', targetMonth] as const,
  gaCourierSettlements: (targetMonth: string) => ['ga-courier', 'settlements', targetMonth] as const,
  /** 예약 가능한 자산(차량·회의실). 종류 탭이 곧 키다. */
  assetItems: (assetType: string) => ['asset-items', assetType] as const,
  /** 예약은 한 주씩 본다 — 주가 바뀌면 다른 목록이다. */
  assetReservations: (weekStart: string) => ['asset-reservations', weekStart] as const,
  /** 자산 마스터(관리 화면). 끈 자산까지 보므로 예약 화면의 `assetItems` 와 키를 나눈다. */
  assetMasters: (assetType: string) => ['asset-masters', assetType] as const,
  /** 총무 운영 기준값(퀵 한도·차량 임계). 회사 전체에 하나뿐이다. */
  gaSettings: ['ga-settings'] as const,
  /** 소프트웨어 라이선스(V74). 끈 라이선스까지 볼지가 곧 다른 목록이다. */
  softwareLicenses: (activeOnly: boolean) => ['software-licenses', activeOnly] as const,
  /** 라이선스 신청(V265). 처리 전만 볼지가 곧 다른 목록이다. */
  softwareRequests: (openOnly: boolean) => ['software-requests', openOnly] as const,
  /** 배포 내역(V265). 제품·검색어·쪽·쪽 크기·정렬이 곧 다른 목록이다. */
  softwareHistory: (licenseId: string | null, keyword: string, page: number, size: number, sort: string, dir: string) =>
    ['software-history', licenseId ?? 'all', keyword, page, size, sort, dir] as const,
  /** 현황 표의 줄들(V271 — 라이선스 1개가 앞, 배포가 뒤). 제품 필터가 곧 키다. */
  softwareBoard: (licenseId: string | null, activeOnly: boolean) =>
    ['software-board', licenseId ?? 'all', activeOnly] as const,
  /** 아직 안 나간 라이선스 1개(V271). 배포 창이 고르는 목록이다. */
  softwareFreeUnits: (licenseId: string | null) => ['software-free-units', licenseId ?? 'none'] as const,
  /** 그 사람에게 지급된 PC(V334). 배포 창이 등록 PC 를 고르는 목록이라 사람이 곧 키다. */
  softwareStaffPcs: (personnelId: string | null) => ['software-staff-pcs', personnelId ?? 'none'] as const,
  /** 퇴사자 라이선스 해제 요청(V334). 처리 전만 볼지가 곧 다른 목록이다. */
  softwareReleases: (openOnly: boolean) => ['software-releases', openOnly] as const,
  /** 「내가 해제를 승인할 수 있는가」. 목록과 키를 나눈다 — 판정은 목록 없이도 물을 수 있어야 한다. */
  softwareReleaseAccess: ['software-release-access'] as const,
  /**
   * 「거뒀는데 아직 돌고 있다」(V335) — 회수일 뒤에 그 제품이 켜진 흔적.
   *
   * <p>해제 목록(`software-releases`)과 키를 나눈다. 승인해 거둔 <b>뒤에</b> 보는 자료라, 처리 전
   * 목록과 함께 비워지면 방금 승인한 줄이 여기 나타나는 것을 볼 수 없다.
   */
  softwareStillRunning: ['software-still-running'] as const,
  /** 내가 라이선스 키를 넣고 볼 수 있는가(V266 · 공사전략팀). 계정이 바뀌면 세션과 함께 비워진다. */
  softwareKeyAccess: ['software-key-access'] as const,
  /** 안내양식 탭(V256) — 불출 메일 양식과 제품마다 1개당 허용 인원. 회사 전체에 한 벌이다. */
  softwareMailForm: ['software-mail-form'] as const,
  /** 키를 연 기록(V266). 담당자 화면에서만 읽는다 — 한 쪽씩. */
  softwareKeyViews: (licenseId: string | null, page: number, size: number, sort: string, dir: string) =>
    ['software-key-views', licenseId ?? 'all', page, size, sort, dir] as const,
  /** 자산 이동 원장(V74). 상태 필터가 곧 키다. */
  assetMovements: (status: string) => ['asset-movements', status] as const,
  /** 자재 단가 설정(V143). 끈 줄까지 보는 관리 화면의 목록이라 위젯의 `market` 과 키를 나눈다. */
  materialPrices: ['material-prices'] as const,

  /**
   * 소속(부서·현장)별 표시 색(V295). 직원 자산 현황의 이름 테두리가 읽는다.
   *
   * <p>설정 창의 목록(`deptColorUnits`)과 키를 나눈다 — 그쪽은 색이 없는 소속과 인원까지 담은
   * 무거운 목록이고, 표가 쓰는 것은 「이름 → 색」 한 벌이다.
   */
  deptColors: ['dept-colors'] as const,
  deptColorUnits: ['dept-colors', 'units'] as const,

  /** 식대 체크 — 그날 명단(V294). 날짜가 곧 다른 목록이다. */
  mealCheckins: (date: string) => ['meal-checkins', date] as const,
  /** 날짜별 인원. 달을 한 표로 보는 자리라 구간이 곧 키다. */
  mealCounts: (from: string, to: string) => ['meal-counts', from, to] as const,
  /** 식당 설정(인정 시간대·QR 토큰). 회사 전체에 하나뿐이다. */
  mealSettings: ['meal-settings'] as const,
  /** 본인 기록. 계정이 바뀌면 세션과 함께 비워진다. */
  mealMine: ['meal-mine'] as const,

  /**
   * 협력사·감리 마스터(V245). <b>접두어와 낱개를 함께 둔다</b> — 필터가 곧 키라 낱개로만 털면
   * 지금 보고 있는 조합 하나만 새로 읽고, 구분을 바꿨을 때 방금 고친 업체가 옛 모습으로 돌아온다.
   */
  vendorsAll: ['vendors'] as const,
  vendors: (partyType: string, keyword: string, activeOnly: boolean) =>
    ['vendors', partyType, keyword, activeOnly] as const,
  /** 아직 마스터에 없는 업체 문자열(DOC-82 §2-5). 확정하면 이 목록에서 빠진다. */
  vendorCandidates: ['vendor-candidates'] as const,
  /** 문자열 → 마스터 전환 진척. 2단계 완료 조건(NULL 5% 미만)을 화면이 이것으로 본다(DOC-82 §9). */
  vendorLinkProgress: ['vendor-link-progress'] as const,
  /** 협력사 등록 신청(V345 · DOC-96). 접두어로 털면 「접수 중」 배지와 「전체」 목록이 함께 새로 읽힌다. */
  vendorApplicationsAll: ['vendor-applications'] as const,
  vendorApplications: (status: string) => ['vendor-applications', status] as const,
  /** 협력사 마스터 › 직원 접속(V369). 포털에 들어올 수 있는 직원 목록. */
  portalStaff: ['portal-staff'] as const,
  /** 파트너 페이지 문의(V357 · DOC-96 §9-4). 접두어로 털면 「답 안 함」 배지와 「전체」 목록이 함께 새로 읽힌다. */
  vendorInquiriesAll: ['vendor-inquiries'] as const,
  vendorInquiries: (status: string) => ['vendor-inquiries', status] as const,

  /**
   * 견적·입찰(V358 · DOC-98 §5-2). <b>접두어와 낱개를 함께 둔다</b> — 공고·개찰·낙찰 한 번이
   * 목록의 상태·응찰수와 상세를 동시에 바꾸므로, 쓰기 뒤에는 접두어로 털어 둘을 함께 새로 읽는다.
   *
   * <p>상태 필터가 곧 목록 키다. 「작성중」만 보다가 공고하면 그 줄이 빠져야 하는데, 낱개 키가
   * 아니면 방금 공고한 건이 작성중 목록에 그대로 남는다.
   */
  pmisRfqsAll: ['pmis-rfqs'] as const,
  pmisRfqs: (siteId: string, status: string) => ['pmis-rfqs', siteId, status] as const,
  pmisRfq: (siteId: string, id: string) => ['pmis-rfqs', siteId, 'one', id] as const,
  /**
   * 전 현장 보드(2026-09-30 · 현장 현황 › 입찰 › 견적). <b>같은 접두어 아래</b> 둔다 — 공고 하나를
   * 고치면 그 현장 목록과 이 보드가 함께 털려야 한다. 현장 id 가 들 자리에 `board` 를 놓아 현장
   * 하나의 목록과 섞이지 않는다.
   */
  pmisRfqBoard: (status: string) => ['pmis-rfqs', 'board', status] as const,
};
